import React from 'react';
import { Check, CheckCircle, ErrorOutline } from '@mui/icons-material';
import {
  DeficiencyOutlined,
  FlagFilled as Flag,
  LimitationOutlined,
  Monitor,
  NotPresent,
  Repair,
  Safety
} from '@inspectify/design';
import { IssueType } from "../../types/report";
import { pluralize, ReportItemType, ReportSeverity, ReportStatusEnum, ReportType } from '@inspectify/core';

export function getSubSystemStatusIcon(status: ReportStatusEnum) {
  switch (status) {
    case 'inspected':
      return <Check />;
    case 'not_present':
      return <NotPresent />;
    case 'issue':
      return <Check />;
    case 'cannot_inspect':
    default:
      return <ErrorOutline />;
  }
}

export function getSubSystemBadgeProps(type: string, count: number, hideDeficiencies = false, noDeficienciesAllowed = false) {
  let text = '';
  let icon = undefined;
  let color = 'default';
  switch (type) {
    case ReportStatusEnum.Fixed:
      text = 'Fixed';
      icon = <Check />;
      color = type as ReportStatusEnum;
      break;
    case ReportStatusEnum.NotPresent:
      text = 'Not present';
      icon = <NotPresent />;
      color = type as ReportStatusEnum;
      break;
    case ReportStatusEnum.InProgress:
      text = 'In progress';
      icon = <ErrorOutline />;
      color = type as ReportStatusEnum;
      break;
    case 'cannot_inspect':
      text = 'Cannot inspect';
      icon = <NotPresent />;
      color = type as ReportStatusEnum;
      break;
    case 'limitation':
      text = 'Limitation';
      icon = <LimitationOutlined />;
      color = type as ReportStatusEnum;
      break;
    case 'issue':
      if (hideDeficiencies || (noDeficienciesAllowed && count === 0)) {
        text = 'Inspected';
        icon = <Check />;
        color = type as ReportStatusEnum;
        break;
      } else if (count === 0) {
        text = 'No issues';
        icon = <Check />;
        color = 'success';
        break;
      }
      text = pluralize(`${count} issue`, count);
      icon = <DeficiencyOutlined />;
      color = 'info';
      break;
    case ReportSeverity.Safety:
      text = 'Safety deficiency';
      icon = <Safety />;
      color = type;
      break;
    case ReportSeverity.Repair:
      text = 'Repair deficiency';
      icon = <Repair />;
      color = type;
      break;
    case ReportSeverity.Monitor:
      text = 'Monitor deficiency';
      icon = <Monitor />;
      color = type;
      break;
    case ReportSeverity.InspectifyFlag:
      text = 'Inspectify flag';
      icon = <Flag />;
      color = type;
      break;
    case ReportStatusEnum.Inspected:
      text = 'Inspected';
      icon = <Check />;
      color = type as ReportStatusEnum;
      break;
    case ReportStatusEnum.NotStarted:
      text = 'Not inspected';
      icon = <ErrorOutline />;
      color = type as ReportStatusEnum;
      break;
    case ReportStatusEnum.CannotInspect:
      text = 'Cannot inspect';
      icon = <ErrorOutline />;
      color = type as ReportStatusEnum;
      break;
    default:
      text = 'No Issues';
      icon = <Check />;
      color = type as ReportStatusEnum;
      break;
  }
  return { text, icon, color };
}

export const getIssueIcon = (type: IssueType, withColor = false) => {
  let icon = undefined;
  switch (type) {
    case IssueType.safety:
      // @ts-ignore
      icon = <Safety color={withColor ? 'safety' : 'inherit'} />;
      break;
    case IssueType.repair:
      // @ts-ignore
      icon = <Repair color={withColor ? 'repair' : 'inherit'} />;
      break;
    case IssueType.monitor:
      // @ts-ignore
      icon = <Monitor color={withColor ? 'monitor' : 'inherit'} />;
      break;
    case IssueType.flag:
      // @ts-ignore
      icon = <Flag color={withColor ? 'inspectify_flag' : 'inherit'} />;
      break;
    case IssueType.fixed:
      icon = <CheckCircle color={withColor ? 'fixed' : 'inherit'} />;
      break;
    case IssueType.limitation:
      // @ts-ignore
      icon = <LimitationOutlined color={withColor ? 'limitation' : 'inherit'} />;
      break;
    default:
      return undefined;
  }
  return icon;
};

// Looks for the parent of the item by id, then returns the parent system.
export const findItemSystem = (currentItem: ReportType | ReportItemType, parentItemId: string) => {
  if (currentItem.id === parentItemId) {
    return true;
  }

  if ('reportSystems' in currentItem) {
    for (const reportSystem of currentItem.reportSystems) {
      if (findItemSystem(reportSystem, parentItemId)) {
        return reportSystem;
      }
    }
  }

  if ('reportSubsystems' in currentItem) {
    for (const reportSubsystem of currentItem.reportSubsystems) {
      if (findItemSystem(reportSubsystem, parentItemId)) {
        return true;
      }
    }
  }

  if ('reportInformations' in currentItem) {
    for (const reportInformation of currentItem.reportInformations) {
      if (findItemSystem(reportInformation, parentItemId)) {
        return true;
      }
    }
  }

  if ('reportDeficiencies' in currentItem) {
    for (const reportDeficiency of currentItem.reportDeficiencies) {
      if (findItemSystem(reportDeficiency, parentItemId)) {
        return true;
      }
    }
  }

  if ('reportLimitations' in currentItem) {
    for (const reportLimitation of currentItem.reportLimitations) {
      if (findItemSystem(reportLimitation, parentItemId)) {
        return true;
      }
    }
  }

  return null;
}
